<!--
    MAIN DYNAMIC DASHBOARD FOR CONDUCTING CONTOUR CHECK, ANALYSIS AND GENERATING REPORT.
    COMPONENT PART OF © AREA 19, OCG WEB APP.
    DEVELOPED IN MONTREAL, QC, CANADA.
    JULY 2023.
  -->

<script>
import GeneralPurposeContainer from '@src/components/GeneralPurposeContainer.vue';

import AnalysisNavigationBar from '../components/AnalysisNavigationBar.vue';
import AnalysisViewSkeleton from '../components/AnalysisViewSkeleton.vue';
import FlowComponent from '../components/FlowComponent.vue';
import FunctionComponent from '../components/FunctionComponent.vue';
import ReportMaker from '../components/report-generator/ReportMaker.vue';
import VersionNumber from '../components/VersionNumber.vue';
import ViewerComponent from '../components/ViewerComponent.vue';
import { useAppState } from '../store/ocg-app-state';
import { useGeneralPurposeOverlayManager } from '../store/overlay-management/overlay-manager';
import GraphicalAnalysis from './GraphicalAnalysis.vue';
import QualityCheck from './QualityCheck.vue';

export default {
  components: {
    AnalysisNavigationBar,
    AnalysisViewSkeleton,
    QualityCheck,
    ReportMaker,
    GraphicalAnalysis,
    ViewerComponent,
    VersionNumber,
    FunctionComponent,
    FlowComponent,
    GeneralPurposeContainer,
  },
  data() {
    const overlayManager = useGeneralPurposeOverlayManager();

    return {
      // Data properties
      appState: useAppState(),
      overlayManager,
      overlayState: overlayManager.getGeneralPurposeOverlayState(),
    };
  },
  computed: {
    isInlineOverlayOpen() {
      return (
        this.overlayState.positionType === 'inline' && this.overlayState.isOpen
      );
    },
    currentView() {
      return this.appState.currentView;
    },
    activeOrPendingView() {
      return this.appState.pendingView || this.appState.currentView;
    },
    isFirstMountTarget() {
      return !this.appState.mountedViews?.has(this.activeOrPendingView);
    },
  },
  watch: {
    currentView: {
      immediate: true,
      handler(newView) {
        if (newView === 'ReportMaker') {
          this.overlayManager.showGeneralPurposeOverlay({ viewerType: 'clipboard' });
        }
        else if (newView === 'ViewerComponent') {
          this.overlayManager.showGeneralPurposeOverlay({ viewerType: 'viewer' });
        }
        else if (newView === 'GraphicalAnalysis') {
          this.overlayManager.closeGeneralPurposeOverlay();
        }
      },
    },
  },
  created() {
    this.overlayManager.setGeneralPurposePositionType('inline');

    // Set the time of day and update the container class accordingly
    if (this.isAnalysisRoute) {
      this.appState.setView('QualityCheck');
    }
  },
};
</script>

<template>
  <div
    class="main-analysis-layout flex w-full h-full"
    :class="{ 'main-analysis-layout--overlay-open': isInlineOverlayOpen }"
  >
    <div
      class="main-analysis-content relative"
      :class="{ 'main-analysis-content--overlay-open': isInlineOverlayOpen }"
    >
      <!-- Skeleton loader overlay layer: displays during navigation / initial mounting -->
      <transition name="skeleton-fade">
        <AnalysisViewSkeleton
          v-if="appState.isViewLoading"
          :target-view="appState.pendingView || appState.currentView"
          class="skeleton-overlay"
        />
      </transition>

      <!-- Active Content Viewport Layer with KeepAlive -->
      <div
        class="view-viewport h-full w-full"
        :class="{ 'view-viewport--hidden': appState.isViewLoading && isFirstMountTarget }"
      >
        <KeepAlive>
          <component :is="appState.currentView" />
        </KeepAlive>
      </div>
    </div>

    <div
      class="general-purpose-pane"
      :class="{ 'general-purpose-pane--open': isInlineOverlayOpen }"
    >
      <GeneralPurposeContainer id="Contours" />
    </div>
  </div>

  <VersionNumber :margin-bot="34" />
</template>

<style scoped>
.main-analysis-layout {
  --general-purpose-inline-width: 412px;
  min-height: 0;
  column-gap: 0;
  transition: column-gap 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.main-analysis-layout--overlay-open {
  column-gap: 0.5rem;
}

.main-analysis-content {
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  position: relative;
  overflow: hidden;
  transition: flex-basis 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.main-analysis-content--overlay-open {
  flex: 1 1 calc(100% - var(--general-purpose-inline-width));
}

.general-purpose-pane {
  display: flex;
  height: 100%;
  width: 0;
  flex: 0 0 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  transition:
    width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1),
    flex-basis 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.general-purpose-pane--open {
  width: var(--general-purpose-inline-width);
  flex: 0 0 var(--general-purpose-inline-width);
}

/* Skeleton overlay styles */
.skeleton-overlay {
  position: absolute;
  inset: 0;
  z-index: 30;
  width: 100%;
  height: 100%;
  background: var(--md-surface, #141414);
  pointer-events: auto;
}

.skeleton-fade-enter-active {
  transition: opacity 0.05s ease-out;
}

.skeleton-fade-leave-active {
  transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.skeleton-fade-enter-from,
.skeleton-fade-leave-to {
  opacity: 0;
}

/* View transition styles */
.view-viewport {
  position: relative;
  min-width: 0;
  min-height: 0;
  transition: opacity 0.2s ease;
}

.view-viewport--hidden {
  opacity: 0;
  pointer-events: none;
}
</style>
